import { type ReactNode } from "react";

import { CoinvestDocumentType } from "@vega-api/generated/types/coinvestDocumentType";
import { type NdaDealViewResponseV3 } from "@vega-api/generated/types/ndaDealViewResponseV3";
import {
  PDF_MIME_TYPES,
  WORD_MIME_TYPES,
} from "@vega-core/constants/documents";
import { m } from "@vega-core/paraglide/messages";

import { useCoInvestDealReadOnly } from "../../hooks/useCoInvestDealReadOnly";

import { NdaUploadInput } from "./NdaUploadInput";

const PDF_ACCEPT = Object.keys(PDF_MIME_TYPES).join(",");
const WORD_ACCEPT = Object.keys(WORD_MIME_TYPES).join(",");

type NdaUploadSectionProps = {
  nda?: NdaDealViewResponseV3;
  onPdfFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onWordFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isPendingPdf?: boolean;
  isPendingWord?: boolean;
  pdfInputRef: React.RefObject<HTMLInputElement | null>;
  wordInputRef: React.RefObject<HTMLInputElement | null>;
  heading?: ReactNode;
};

export const NdaUploadSection = ({
  nda,
  onPdfFileSelect,
  onWordFileSelect,
  isPendingPdf = false,
  isPendingWord = false,
  pdfInputRef,
  wordInputRef,
  heading,
}: NdaUploadSectionProps) => {
  const { isMutationLocked: isUploadHidden } = useCoInvestDealReadOnly();

  const pdfDoc = nda?.documents.find(
    (d) => d.typeOfDocument === CoinvestDocumentType.PDF,
  );
  const wordDoc = nda?.documents.find(
    (d) => d.typeOfDocument === CoinvestDocumentType.WORD,
  );

  const defaultHeading = (
    <h2 className="heading-5 text-text/primary mb-4">
      {m.coInvestments_configureNda()}
    </h2>
  );

  return (
    <div>
      {heading !== undefined ? heading : defaultHeading}
      <div className="space-y-4">
        <NdaUploadInput
          accept={PDF_ACCEPT}
          dataTestId="select-nda-pdf-file"
          document={pdfDoc}
          inputId="nda-upload"
          inputRef={pdfInputRef}
          isPending={isPendingPdf}
          onFileSelect={onPdfFileSelect}
          readOnly={isUploadHidden}
          title={m.coInvestments_pdfOnly()}
        />
        <NdaUploadInput
          accept={WORD_ACCEPT}
          dataTestId="select-nda-word-file"
          document={wordDoc}
          inputId="nda-word-upload"
          inputRef={wordInputRef}
          isPending={isPendingWord}
          onFileSelect={onWordFileSelect}
          readOnly={isUploadHidden}
          title={m.coInvestments_wordOnly()}
        />
      </div>
    </div>
  );
};
